import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BUSINESS_QUERY_KEYS } from "@/business/constants";
import { useApi } from "@/core/api";
import { useAuth } from "@/core/auth";
import { businessUsersKey } from "@/user/query-keys";
import type {
  AccessPreview,
  AccessDraft,
  DraftReview,
  WorkbookAccessSettings,
  WorkingAccess,
} from "./types";

export const workbookAccessSettingsPath = (businessId: string) =>
  `/businesses/${businessId}/workbook_access_settings`;

export const workbookAccessSettingsKey = (
  businessId: string | null,
  userId?: string,
) => ["workbook-access-settings", businessId, userId] as const;

const toBody = (working: WorkingAccess) => ({
  assignments: working.assignments,
  key_settings: working.keySettings,
});

export const useWorkbookAccessSettings = (businessId: string | null) => {
  const api = useApi();
  const { user } = useAuth();

  return useQuery({
    queryKey: workbookAccessSettingsKey(businessId, user?.id),
    queryFn: ({ signal }) =>
      api
        .get<WorkbookAccessSettings>(workbookAccessSettingsPath(businessId!), {
          signal,
        })
        .then((response) => response.data),
    enabled: Boolean(businessId),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

const useStoreSettings = (businessId: string | null) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return (settings: WorkbookAccessSettings) =>
    queryClient.setQueryData(
      workbookAccessSettingsKey(businessId, user?.id),
      settings,
    );
};

// A refused write may mean the live access moved on (another owner, an admin
// publish, the daemon). Reloading puts the current access on screen, and the
// next attempt cites its digests instead of failing the same way.
const useReloadSettings = (businessId: string | null) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return () =>
    queryClient.invalidateQueries({
      queryKey: workbookAccessSettingsKey(businessId, user?.id),
    });
};

export const usePreviewWorkbookAccess = (businessId: string | null) => {
  const api = useApi();
  return useMutation({
    mutationFn: (working: WorkingAccess) =>
      api
        .post<{
          preview: AccessPreview;
        }>(
          `${workbookAccessSettingsPath(businessId!)}/preview`,
          toBody(working),
        )
        .then((response) => response.data.preview),
  });
};

export const useSaveWorkbookAccess = (businessId: string | null) => {
  const api = useApi();
  const store = useStoreSettings(businessId);
  const reload = useReloadSettings(businessId);
  return useMutation({
    mutationFn: ({
      working,
      settings,
    }: {
      working: WorkingAccess;
      settings: WorkbookAccessSettings;
    }) =>
      api
        .post<WorkbookAccessSettings>(
          `${workbookAccessSettingsPath(businessId!)}/draft`,
          {
            ...toBody(working),
            draft_id: settings.draft?.id,
            draft_digest: settings.draft?.content_digest,
            base_digest: settings.base_digest,
            base_key_settings_digest: settings.base_key_settings_digest,
          },
        )
        .then((response) => response.data),
    onSuccess: store,
    onError: () => void reload(),
  });
};

export const useMakeWorkbookOwner = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const store = useStoreSettings(businessId);
  const reload = useReloadSettings(businessId);
  return useMutation({
    mutationFn: (userId: string) =>
      api
        .post<WorkbookAccessSettings>(
          `${workbookAccessSettingsPath(businessId!)}/owner`,
          { user_id: userId },
        )
        .then((response) => response.data),
    onSuccess: (settings) => {
      store(settings);
      void queryClient.invalidateQueries({
        queryKey: businessUsersKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: [BUSINESS_QUERY_KEYS.currentUserWorkbookAccess],
      });
    },
    onError: () => void reload(),
  });
};

export const useReviewAccessDraft = (businessId: string) => {
  const api = useApi();
  const reload = useReloadSettings(businessId);
  return useMutation({
    mutationFn: (draft: AccessDraft) =>
      api
        .post<DraftReview>(`${workbookAccessSettingsPath(businessId)}/review`, {
          draft_id: draft.id,
          draft_digest: draft.content_digest,
        })
        .then((response) => response.data),
    onError: () => void reload(),
  });
};

export const useApplyAccessDraft = (businessId: string) => {
  const api = useApi();
  const store = useStoreSettings(businessId);
  const reload = useReloadSettings(businessId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (review: DraftReview) =>
      api
        .post<WorkbookAccessSettings>(
          `${workbookAccessSettingsPath(businessId)}/apply`,
          {
            draft_id: review.draft_id,
            reviewed_digest: review.reviewed_digest,
          },
        )
        .then((response) => response.data),
    onSuccess: (settings) => {
      store(settings);
      void queryClient.invalidateQueries({ queryKey: ["payroll-roster"] });
      void queryClient.invalidateQueries({
        queryKey: [BUSINESS_QUERY_KEYS.currentUserWorkbookAccess],
      });
    },
    onError: () => void reload(),
  });
};

export const useDiscardAccessDraft = (businessId: string) => {
  const api = useApi();
  const store = useStoreSettings(businessId);
  const reload = useReloadSettings(businessId);
  return useMutation({
    mutationFn: (draft: AccessDraft) =>
      api
        .delete<WorkbookAccessSettings>(
          `${workbookAccessSettingsPath(businessId)}/draft`,
          {
            data: { draft_id: draft.id },
          },
        )
        .then((response) => response.data),
    onSuccess: store,
    onError: () => void reload(),
  });
};
